import {Component, OnDestroy, OnInit, signal} from '@angular/core';
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {User} from '../../classes/user.class';
import {UserAssignment} from '../../classes/user-assignment.class';
import {UserService} from '../../services/user/user.service';
import {MatFormField, MatHint, MatInput, MatPrefix, MatSuffix} from '@angular/material/input';
import {MatButton, MatIconButton} from '@angular/material/button';
import {FieldErrorComponent} from '../../field-error/field-error.component';
import {Utils} from '../../utils.class';
import {CodaltComponent} from '../../codalt.component';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {Routenames} from '../../classes/route-name.enum';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {NgSelectComponent} from '@ng-select/ng-select';
import {Roles} from '../../services/auth/roles';
import {Role} from '../../classes/auth/role.class';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {DecimalPipe} from '@angular/common';
import {BreadcrumbsComponent} from '../../shared/ui/breadcrumbs/breadcrumbs.component';
import {FieldComponent} from '../../shared/ui/field/field.component';
import {PageComponent} from '../../shared/ui/page/page.component';
import {SectionComponent} from '../../shared/ui/section/section.component';
import {StatusBadgeComponent} from '../../shared/ui/status-badge/status-badge.component';
import {StickySaveBarComponent} from '../../shared/ui/sticky-save-bar/sticky-save-bar.component';
import {UserRoleDescriptionsComponent} from '../user-role-descriptions/user-role-descriptions.component';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    imports: [
        MatInput,
        MatFormField,
        MatHint,
        MatPrefix,
        MatSuffix,
        FormsModule,
        ReactiveFormsModule,
        FieldErrorComponent,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatButton,
        MatIconButton,
        NgSelectComponent,
        RouterLink,
        DecimalPipe,
        BreadcrumbsComponent,
        FieldComponent,
        PageComponent,
        SectionComponent,
        StatusBadgeComponent,
        StickySaveBarComponent,
        UserRoleDescriptionsComponent,
    ]
})
export class UserEditComponent extends CodaltComponent implements OnInit, OnDestroy {
    saving = false;
    title = '';

    readonly form = signal<FormGroup | null>(null);
    readonly users = signal<User[]>([]);

    minDate: Date;
    rolesMap: Map<Roles, Role>;
    roles: Role[];

    constructor(
        private activatedRoute: ActivatedRoute,
        private authorisationService: AuthorisationService,
        private confirmDialog: ConfirmDialogService,
        private userService: UserService,
        private router: Router,
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.authorisationService.getRolesMap().subscribe(rolesMap => {
                this.rolesMap = rolesMap;
                this.roles = Array.from(rolesMap.values())
                    .filter(r => !r.restricted || AuthorisationService.hasRole(Roles.Management))
                    .sort((a, b) => roleOrder(a.key) - roleOrder(b.key));

                this.subscriptions.add(
                    this.activatedRoute.params.subscribe((params: {id}) => {
                        if (params.id === 'new') {
                            this.createForm(new User());
                        } else {
                            this.subscriptions.add(
                                this.userService.getUser(+params.id).subscribe({
                                    next: res => this.createForm(res),
                                    error: err => console.error(err)
                                })
                            );
                        }
                    })
                );
            })
        );

    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            const user = new User();
            Object.assign(user, this.form().value);
            user.assignments = this.assignments.value.map(assignment => ({
                id: assignment.id,
                begin_date: Utils.formatDate(assignment.begin_date),
                end_date: assignment.end_date ? Utils.formatDate(assignment.end_date) : null,
                part_time_rate: parseFloat((assignment.part_time_rate / 100).toFixed(2))
            }));
            this.subscriptions.add(
                this.userService.saveUser(user).subscribe({
                    next: () => this.router.navigate([Routenames.users]),
                    error: err => console.error(err)

                })
            );
        }
    }

    get assignments() {
        return this.form().get('assignments') as FormArray<FormGroup>;
    }

    addAssignment() {
        // Continue after the last period, the first period starts at the begin of the employment
        const last = this.assignments.at(this.assignments.length - 1)?.value;
        let begin = last ? null : this.form().get('employment_start').value;
        if (last?.end_date) {
            begin = new Date(last.end_date);
            begin.setDate(begin.getDate() + 1);
        }
        this.assignments.push(this.assignmentGroup({
            begin_date: begin ? Utils.formatDate(begin) : null,
            part_time_rate: last ? last.part_time_rate / 100 : 1
        }));
        // push() and removeAt() do not mark the form dirty, the save bar needs it to enable saving
        this.assignments.markAsDirty();
    }

    removeAssignment(index: number) {
        this.assignments.removeAt(index);
        this.assignments.markAsDirty();
    }

    /** Whether a period (form value with Date values) covers today, for the "Huidig" label. */
    isCurrentPeriod(period: {begin_date: Date | null, end_date: Date | null}): boolean {
        const today = Utils.formatDate(new Date());
        return !!period.begin_date && Utils.formatDate(period.begin_date) <= today &&
            (!period.end_date || Utils.formatDate(period.end_date) >= today);
    }

    cancel() {
        this.router.navigate([Routenames.users]);
    }

    deleteUser(event) {
        event.preventDefault();
        event.stopPropagation();

        const user = new User();
        Object.assign(user, this.form().value);

        this.confirmDialog.confirm(
            'Verwijderen',
            `Weet u zeker dat u de gebruiker ${user.name} wilt verwijderen?`,
            'Ja',
            'nee'
        )
            .then(() => {
                this.userService.deleteUser(user.id).then(() => {
                    this.router.navigate([Routenames.users]);
                });
            })
            .catch(() => {
                console.log('user not deleted')
            });
    }

    private createForm(user: User) {
        this.crumbs = [
            {name: 'Gebruikers', route: Routenames.users},
            {name: `Gebruiker : ${user?.name || 'nieuw'}`, route: `${Routenames.users}/${user?.id || 'new'}`}
        ];

        this.title = user.id ? 'aanpassen' : 'toevoegen';
        this.form.set(new FormGroup({
            id: new FormControl(user.id),
            name: new FormControl(user.name, [Validators.required, Validators.minLength(3)]),
            email: new FormControl(user.email, [Validators.required, Validators.email]),
            hourly_rate: new FormControl(user.hourly_rate),
            assignments: new FormArray((user.assignments ?? []).map(assignment => this.assignmentGroup(assignment)), UserAssignment.validatePeriods),
            employment_start: new FormControl(user.employment_start),
            employment_end: new FormControl(user.employment_end),
            period_costs: new FormControl(user.period_costs),
            roles: new FormControl(user.roles ? user.roles.map(r => r.key) : []),
            updated_at: new FormControl(user.updated_at)
        }));
    }

    private assignmentGroup(assignment: Partial<UserAssignment>) {
        return new FormGroup({
            id: new FormControl(assignment.id),
            begin_date: new FormControl(assignment.begin_date ? new Date(`${assignment.begin_date}T00:00`) : null, Validators.required),
            end_date: new FormControl(assignment.end_date ? new Date(`${assignment.end_date}T00:00`) : null),
            part_time_rate: new FormControl(Math.round(assignment.part_time_rate * 100), [Validators.required, Validators.min(0), Validators.max(100)])
        });
    }
}

/** Order of the roles in the form, as in the design; roles not listed there follow. */
const ROLE_ORDER: Roles[] = [
    Roles.Employee,
    Roles.Management,
    Roles.SeireManagement,
    Roles.MutationPreviousPeriod,
    Roles.GlobalReports,
    Roles.PdfUnderlay,
    Roles.Tester,
];

function roleOrder(key: Roles): number {
    const index = ROLE_ORDER.indexOf(key);
    return index === -1 ? ROLE_ORDER.length : index;
}
